import { Badge } from "@components/ui/badge"
import {
  useAccount,
  useGetLinkedAccountsQuery,
} from "@apis/accounts/useAccounts"
import EditMemberTraits from "../Traits/EditMemberTraits"
import { useDrawer } from "@contexts/DrawerContext"
import { CircleAlert, ExternalLink, InfoIcon } from "lucide-react"
import { useHasPermissions } from "../../../hooks/useHasPermissions"
import { LinkedAccounts } from "./LinkedAccounts"
import ServiceSummary from "@components/AccountNew/ServiceSummary/ServiceSummary"
import { useGetProspectQuery } from "@apis/prospects/useProspects"
import AccountCasesContainer from "@components/account/accountCases/AccountCasesContainer"

export const useAccountTitleButtonsSerializer = (account_id: string) => {
  const getLinkedAccountsQuery = useGetLinkedAccountsQuery(account_id)
  const getAccountQuery = useAccount(account_id)
  const getProspectQuery = useGetProspectQuery(
    getAccountQuery.data?.prospect_guid,
  )

  const { openDrawer } = useDrawer()
  const { canSeeBraze, canSeeTitanium, roleCanOpenCase } = useHasPermissions()

  const externalLinkIcon = <ExternalLink className="tw:inline" />

  const data = {
    buttonItems: [
      {
        icon: <InfoIcon size={16} />,
        label: "Summary",
        onClick: () =>
          openDrawer(
            <ServiceSummary
              account_id={account_id}
              accountData={getAccountQuery.data}
            />,
            "Service Summary",
          ),
      },
      {
        label: "Accounts",
        onClick: () =>
          openDrawer(
            <LinkedAccounts account_id={account_id} />,
            "Linked Accounts",
          ),
        badge: (() => {
          if (getLinkedAccountsQuery.isError) {
            return (
              <Badge>
                <CircleAlert className="tw:text-red-500" />
              </Badge>
            )
          }

          if (getLinkedAccountsQuery.data?.length) {
            return <Badge>{getLinkedAccountsQuery.data.length}</Badge>
          }

          return ""
        })(),
      },
      {
        label: "Traits",
        onClick: () =>
          openDrawer(
            <EditMemberTraits
              prospect_guid={getAccountQuery.data?.prospect_guid}
            />,
            "Traits",
          ),
      },
      {
        label: "Cases",
        onClick: () =>
          openDrawer(
            <AccountCasesContainer
              account={getAccountQuery.data}
              prospect={null}
            />,
            "Cases",
          ),
        permissions: roleCanOpenCase,
      },
      {
        label: "Prospect",
        href: `/prospect/${getAccountQuery.data?.prospect_guid}`,
        openNewWindow: false,
      },
      {
        label: "Membership Profile",
        href: `/profiles/${getAccountQuery.data?.membership_profile_id}`,
        openNewWindow: false,
      },
      {
        label: "Titanium",
        href: import.meta.env.VITE_TITANIUM_SEARCH_URL,
        permissions: !canSeeTitanium,
        icon: externalLinkIcon,
      },
      {
        label: "Braze",
        href: `https://dashboard-06.braze.com/users/user_search/${import.meta.env.VITE_BRAZE_WORKSPACE_ID}?query=${getAccountQuery.data?.prospect_guid}`,
        permissions: !canSeeBraze,
        icon: externalLinkIcon,
      },
      {
        label: "Refer a Friend",
        href: `${import.meta.env.VITE_EXTOLE_SITE}/profiles/view#${getProspectQuery.data?.extole_person_id}`,
        permissions: !getProspectQuery.data?.extole_person_id,
        icon: externalLinkIcon,
      },
    ],
    member_full_name:
      !getAccountQuery.data?.membership_profile.first_name &&
      !getAccountQuery.data?.membership_profile.last_name
        ? "Member"
        : `${getAccountQuery.data?.membership_profile.first_name} ${getAccountQuery.data.membership_profile.last_name}`,
    prospect_guid: getAccountQuery.data?.prospect_guid,
    status_description: getAccountQuery.data?.status_description,
    account_status_code: getAccountQuery.data?.account_status_code,
    is_escalation: getAccountQuery.data?.is_escalation,
    business_name: getAccountQuery.data?.business_name,
  }

  return {
    data,
    isError: getAccountQuery.isError,
    isPending: getAccountQuery.isPending,
    error: getAccountQuery.error,
  }
}
